import { useQuery } from "@tanstack/react-query";
import { useProperty } from "../../../../components/property-provider";
import { useApiClient } from "../../../../lib/http";
import { OccupancyOptionsEntity } from "../../../occupancyTypes/domain/OccupancyOptions.entity";
import { usePropertyConfigurationApi } from "../../../occupancyTypes/infrastructure/api/propertyConfiguration.api";
import { bookingFormConfigurationQueryKeys } from "../cache/BookingFormConfigurationCacheStrategy";

export interface PropertyMaxOccupancy {
  maxAdults?: number;
  maxJuniors?: number;
  maxChildren?: number;
  maxBabies?: number;
}

interface BookingFormConfiguration {
  maxOccupancy: PropertyMaxOccupancy;
  occupancyOptions: OccupancyOptionsEntity | null;
}

export function useBookingFormConfiguration() {
  const propertyInfo = useProperty();
  const apiClient = useApiClient();
  const { getOccupancyOptions } = usePropertyConfigurationApi();

  const { data } = useQuery({
    queryKey: bookingFormConfigurationQueryKeys.config(propertyInfo.ticker),
    queryFn: async (): Promise<BookingFormConfiguration> => {
      const [occupancyOptions, propertyConfiguration] = await Promise.all([
        getOccupancyOptions(),
        apiClient.get<PropertyMaxOccupancy>(`/api/property/${propertyInfo.ticker}/configuration`),
      ]);
      return {
        occupancyOptions,
        maxOccupancy: propertyConfiguration || {},
      };
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return {
    maxOccupancy: data?.maxOccupancy ?? {},
    occupancyOptions: data?.occupancyOptions ?? null,
  };
}
